import React, { useState, useEffect, useCallback } from 'react';
import {
    DollarSign, Users, UserCheck, Mail, FileText, TrendingUp,
    Plus, RefreshCw, Filter, X, Trash2, Settings2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
    fetchCostDashboard, addManualSpend, fetchKnownCampaigns,
    fetchSpendEntries, deleteSpendEntry,
} from '../api/analyticsApi';

// ── formatting helpers ────────────────────────────────────────────────────
const AUD = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
const AUD2 = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 });
const NUM = new Intl.NumberFormat('en-AU');

const fmtMoney = (v) => (v == null ? '—' : AUD.format(v));
const fmtCost = (v) => (v == null ? '—' : AUD2.format(v));   // null = no conversions yet
const fmtNum = (v) => (v == null ? '—' : NUM.format(v));
const fmtPct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);

const PLATFORM_LABELS = { meta: 'Meta', google: 'Google', tiktok: 'TikTok', manual: 'Manual' };

const StatCard = ({ Icon, label, value, sub, accent }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg"
                  style={{ background: `${accent}1a`, color: accent }}>
                <Icon className="w-4 h-4" />
            </span>
            <span className="text-xs font-medium text-gray-500">{label}</span>
        </div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        {sub != null && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
);

// ── add-spend form ─────────────────────────────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);

const OTHER = '__other__';

const AddSpendForm = ({ onClose, onSaved }) => {
    const [form, setForm] = useState({
        platform: 'meta', utm_campaign: '', campaign_name: '',
        date: today(), spend: '',
    });
    const [campaigns, setCampaigns] = useState([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);
    const [campaignsError, setCampaignsError] = useState(false);
    const [selected, setSelected] = useState('');   // chosen utm_campaign, or OTHER, or ''
    const [saving, setSaving] = useState(false);

    // Load the campaigns the system already knows about, so the user picks one
    // instead of typing a UTM.
    useEffect(() => {
        let alive = true;
        setLoadingCampaigns(true);
        setCampaignsError(false);
        fetchKnownCampaigns()
            .then((res) => { if (alive) setCampaigns(res?.campaigns || []); })
            .catch(() => { if (alive) setCampaignsError(true); })
            .finally(() => { if (alive) setLoadingCampaigns(false); });
        return () => { alive = false; };
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    // Picking a known campaign fills in the tag, display name and platform.
    const onSelectCampaign = (e) => {
        const val = e.target.value;
        setSelected(val);
        if (val === OTHER || val === '') {
            setForm((f) => ({ ...f, utm_campaign: '', campaign_name: '' }));
            return;
        }
        const c = campaigns.find((x) => x.utm_campaign === val);
        setForm((f) => ({
            ...f,
            utm_campaign: c.utm_campaign,
            campaign_name: c.name || c.utm_campaign,
            platform: c.platform || f.platform,
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.utm_campaign.trim() || form.spend === '') {
            toast.error('Please choose a campaign and enter the spend.');
            return;
        }
        setSaving(true);
        try {
            await addManualSpend({
                platform: form.platform,
                utm_campaign: form.utm_campaign.trim(),
                campaign_name: form.campaign_name.trim() || undefined,
                date: form.date,
                spend: Number(form.spend),
            });
            toast.success('Spend recorded.');
            onSaved();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not save spend.');
        } finally {
            setSaving(false);
        }
    };

    const field = 'border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-500';

    return (
        <form onSubmit={submit} className="bg-blue-50/60 border border-blue-200 rounded-xl p-4 mb-5">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-gray-800">Add ad spend</h4>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <label className="text-xs font-medium text-gray-600 col-span-2">
                    Campaign *
                    <select value={selected} onChange={onSelectCampaign} className={`${field} mt-1`}>
                        <option value="">
                            {loadingCampaigns ? 'Loading campaigns…' : 'Select a campaign…'}
                        </option>
                        {campaigns.map((c) => (
                            <option key={c.utm_campaign} value={c.utm_campaign}>{c.label}</option>
                        ))}
                        <option value={OTHER}>➕ Other / new campaign…</option>
                    </select>
                    {!loadingCampaigns && campaignsError && (
                        <span className="block text-[11px] text-amber-600 mt-1">
                            Couldn’t load campaigns — pick “Other / new campaign” to enter one.
                        </span>
                    )}
                    {!loadingCampaigns && !campaignsError && campaigns.length === 0 && (
                        <span className="block text-[11px] text-gray-500 mt-1">
                            No campaigns detected from ad traffic yet — use “Other / new campaign”.
                        </span>
                    )}
                </label>
                {selected === OTHER && (
                    <label className="text-xs font-medium text-gray-600 col-span-2">
                        New campaign name *
                        <input
                            value={form.campaign_name}
                            onChange={(e) => setForm((f) => ({ ...f, campaign_name: e.target.value, utm_campaign: e.target.value }))}
                            placeholder="e.g. Spring Sale"
                            className={`${field} mt-1`}
                        />
                    </label>
                )}
                <label className="text-xs font-medium text-gray-600">
                    Platform
                    <select value={form.platform} onChange={set('platform')} className={`${field} mt-1`}>
                        {Object.entries(PLATFORM_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </label>
                <label className="text-xs font-medium text-gray-600">
                    Date
                    <input type="date" value={form.date} onChange={set('date')} max={today()} className={`${field} mt-1`} />
                </label>
                <label className="text-xs font-medium text-gray-600 col-span-2">
                    Spend (AUD) *
                    <input type="number" min="0" step="0.01" value={form.spend} onChange={set('spend')} placeholder="100.00" className={`${field} mt-1`} />
                </label>
                <div className="flex items-end col-span-2">
                    <button type="submit" disabled={saving}
                            className="w-full bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">
                        {saving ? 'Saving…' : 'Save spend'}
                    </button>
                </div>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
                Pick the campaign your spend belongs to — the list shows campaigns the system has already seen from your
                ad traffic. Spend is then matched to the users, signups and enquiries from that campaign.
                Re-saving the same campaign + date for a platform updates that day.
            </p>
        </form>
    );
};

// ── manage / delete recorded spend ──────────────────────────────────────────
const fmtEntryDate = (d) => {
    // d is 'YYYY-MM-DD' — render without timezone shifting.
    const [y, m, day] = (d || '').split('-');
    if (!y) return d;
    return new Date(Number(y), Number(m) - 1, Number(day)).toLocaleDateString('en-AU', {
        day: 'numeric', month: 'short', year: 'numeric',
    });
};

const ManageSpend = ({ onClose, onChanged }) => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            setEntries(await fetchSpendEntries());
        } catch (err) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const remove = async (entry) => {
        const label = `${entry.campaign_name} · ${fmtEntryDate(entry.date)} · ${AUD2.format(entry.spend)}`;
        // eslint-disable-next-line no-restricted-globals, no-alert
        if (!window.confirm(`Delete this spend entry?\n\n${label}\n\nThis can’t be undone.`)) return;
        setDeletingId(entry.id);
        try {
            await deleteSpendEntry(entry.id);
            toast.success('Spend entry deleted.');
            setEntries((rows) => rows.filter((r) => r.id !== entry.id));
            onChanged();   // refresh the dashboard totals/table
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not delete entry.');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-gray-800">Manage recorded spend</h4>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                </button>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Loading entries…
                </div>
            ) : error ? (
                <p className="text-sm text-red-600 py-2">Couldn’t load spend entries.</p>
            ) : entries.length === 0 ? (
                <p className="text-sm text-gray-500 py-2">No spend entries recorded yet.</p>
            ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 text-left">
                                {['Campaign', 'Platform', 'Date', 'Spend', 'Source', ''].map((h) => (
                                    <th key={h} className="px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((e) => (
                                <tr key={e.id} className="border-t border-gray-100">
                                    <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{e.campaign_name}</td>
                                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{PLATFORM_LABELS[e.platform] || e.platform}</td>
                                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtEntryDate(e.date)}</td>
                                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{AUD2.format(e.spend)}</td>
                                    <td className="px-3 py-2 text-gray-400 whitespace-nowrap capitalize">{e.source}</td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        <button
                                            type="button"
                                            onClick={() => remove(e)}
                                            disabled={deletingId === e.id}
                                            className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 disabled:opacity-50 text-xs font-medium"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            {deletingId === e.id ? 'Deleting…' : 'Delete'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="text-[11px] text-gray-500 mt-2">
                Deleting an entry removes that day’s spend from the dashboard. If it’s the last spend behind a
                hand-entered campaign, that campaign is removed too.
            </p>
        </div>
    );
};

// ── funnel ───────────────────────────────────────────────────────────────
const Funnel = ({ stages }) => {
    const top = stages.find((s) => s.count > 0)?.count || 0;
    if (!top) return <p className="text-xs text-gray-400 py-4 text-center">No funnel activity in this range yet.</p>;
    return (
        <div className="space-y-2.5">
            {stages.map((s) => (
                <div key={s.stage} className="flex items-center gap-3">
                    <span className="text-xs text-gray-600 w-36 flex-shrink-0 truncate" title={s.label}>{s.label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                        <div className="h-5 rounded-full bg-blue-600 flex items-center justify-end pr-2"
                             style={{ width: `${Math.max(s.count ? 6 : 0, (s.count / top) * 100)}%` }}>
                            <span className="text-[10px] font-semibold text-white">{s.count ? fmtNum(s.count) : ''}</span>
                        </div>
                    </div>
                    <span className="text-xs text-gray-400 w-12 text-right flex-shrink-0">{fmtPct(s.pct_of_top)}</span>
                </div>
            ))}
        </div>
    );
};

// ── campaign table ─────────────────────────────────────────────────────────
const CampaignTable = ({ rows }) => {
    if (!rows.length) return null;
    const cols = [
        ['Campaign', (r) => <span className="font-medium text-gray-800">{r.campaign_name}</span>],
        ['Platform', (r) => (r.platforms || []).map((p) => PLATFORM_LABELS[p] || p).join(', ') || '—'],
        ['Spend', (r) => fmtMoney(r.spend)],
        ['Clicks', (r) => fmtNum(r.clicks)],
        ['Signups', (r) => fmtNum(r.signups)],
        ['Cost/signup', (r) => fmtCost(r.cost_per_signup)],
        ['Activated', (r) => fmtNum(r.activated_users)],
        ['Cost/activated', (r) => fmtCost(r.cost_per_activated)],
        ['Enquiries', (r) => fmtNum(r.enquiries)],
        ['Cost/enquiry', (r) => fmtCost(r.cost_per_enquiry)],
        ['Offers', (r) => fmtNum(r.offers)],
        ['ROAS', (r) => (r.roas == null ? '—' : `${r.roas}×`)],
    ];
    return (
        <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="min-w-full text-sm">
                <thead>
                    <tr className="bg-gray-50 text-left">
                        {cols.map(([h]) => (
                            <th key={h} className="px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.utm_campaign} className="border-t border-gray-100 hover:bg-gray-50">
                            {cols.map(([h, render]) => (
                                <td key={h} className="px-3 py-2.5 whitespace-nowrap text-gray-700">{render(r)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ── panel ───────────────────────────────────────────────────────────────
const CostPerLeadPanel = ({ startDate, endDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [platform, setPlatform] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [showManage, setShowManage] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchCostDashboard(startDate, endDate, platform || undefined);
            setData(res);
        } catch (err) {
            setError('Could not load cost analytics.');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, platform]);

    useEffect(() => { load(); }, [load]);

    const cards = data?.cards || {};
    // Until real ad spend is recorded the whole table is "(unmatched)" event rows
    // with $0 cost — show the add-spend empty state instead of that noise.
    const hasSpend = (cards.total_spend || 0) > 0;

    return (
        <div className="space-y-5">
            {/* toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-bold text-gray-900">Cost Per Lead</h3>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 text-gray-500">
                        <Filter className="w-4 h-4" />
                        <select value={platform} onChange={(e) => setPlatform(e.target.value)}
                                className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="">All platforms</option>
                            {Object.entries(PLATFORM_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </div>
                    <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                    <button onClick={() => { setShowManage((s) => !s); setShowForm(false); }}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${showManage ? 'bg-gray-100 border-gray-300 text-gray-800' : 'text-gray-600 border-gray-200 hover:bg-gray-100'}`}>
                        <Settings2 className="w-4 h-4" /> Manage spend
                    </button>
                    <button onClick={() => { setShowForm((s) => !s); setShowManage(false); }}
                            className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-blue-700">
                        <Plus className="w-4 h-4" /> Add spend
                    </button>
                </div>
            </div>

            {showForm && <AddSpendForm onClose={() => setShowForm(false)} onSaved={load} />}
            {showManage && <ManageSpend onClose={() => setShowManage(false)} onChanged={load} />}

            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-4">{error}</div>}

            {loading && !data ? (
                <div className="flex items-center justify-center py-16 text-gray-400">
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
            ) : !hasSpend ? (
                <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center">
                    <DollarSign className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700 mb-1">No ad spend recorded for this range yet.</p>
                    <p className="text-xs text-gray-500 mb-4">
                        Add spend (or connect Meta/Google Ads) and it will be matched to signups, activations and
                        enquiries by <code>utm_campaign</code> to compute cost-per-lead.
                    </p>
                    <button onClick={() => setShowForm(true)}
                            className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700">
                        <Plus className="w-4 h-4" /> Add spend
                    </button>
                </div>
            ) : (
                <>
                    {/* cards */}
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                        <StatCard Icon={DollarSign} label="Total spend" value={fmtMoney(cards.total_spend)} accent="#2563eb" />
                        <StatCard Icon={Users} label="New users" value={fmtNum(cards.new_users)} accent="#7c3aed" />
                        <StatCard Icon={DollarSign} label="Cost per user" value={fmtCost(cards.cost_per_user)} accent="#0891b2" />
                        <StatCard Icon={UserCheck} label="Activated users" value={fmtNum(cards.activated_users)}
                                  sub={cards.activation_rate != null ? `${fmtPct(cards.activation_rate)} activation` : null} accent="#16a34a" />
                        <StatCard Icon={DollarSign} label="Cost per activated" value={fmtCost(cards.cost_per_activated_user)} accent="#16a34a" />
                        <StatCard Icon={Mail} label="Property enquiries" value={fmtNum(cards.property_enquiries)} accent="#ea580c" />
                        <StatCard Icon={DollarSign} label="Cost per enquiry" value={fmtCost(cards.cost_per_property_enquiry)} accent="#ea580c" />
                        <StatCard Icon={FileText} label="Offers submitted" value={fmtNum(cards.offers_submitted)} accent="#9333ea" />
                        <StatCard Icon={DollarSign} label="Cost per offer" value={fmtCost(cards.cost_per_offer)} accent="#9333ea" />
                        <StatCard Icon={TrendingUp} label="ROAS"
                                  value={cards.roas == null ? '—' : `${cards.roas}×`}
                                  sub={cards.roas == null ? 'needs revenue tagging' : null} accent="#0d9488" />
                    </div>

                    {/* funnel */}
                    <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <h4 className="text-sm font-bold text-gray-800 mb-4">User journey funnel</h4>
                        <Funnel stages={data.funnel || []} />
                    </div>

                    {/* campaign table */}
                    <div>
                        <h4 className="text-sm font-bold text-gray-800 mb-3">Campaign performance</h4>
                        <CampaignTable rows={data.campaigns || []} />
                    </div>
                </>
            )}
        </div>
    );
};

export default CostPerLeadPanel;
